/**
 * urlNavigation — build + parse drill-down query params for ContentPlansPage.
 *
 * URL schema (mirrors §H.0.3 of the plan):
 *   ?strategy={id}                       → Level 1 (Strategies list / Sprints table)
 *   ?strategy={id}&sprint={id}           → Level 2 (Topics inside sprint)
 *   ?strategy={id}&sprint={id}&topic={id}→ Level 3 (Posts under topic)
 *   ?strategy={id}&post={id}             → Shortcut — opens PostDetailPanel slide-over
 *
 * Using query params (not path segments) keeps browser back/forward, URL-
 * shareable state, and no custom router changes needed.
 */

export interface DrillDownState {
  strategyId?: string;
  sprintId?: string;
  topicId?: string;
  postId?: string;
  /** Sub-tab of ContentPlansPage */
  subTab?: 'strategies' | 'backlog' | 'themes' | 'creators' | 'creator' | 'platforms' | 'templates' | 'series' | 'settings' | 'post-examples' | 'scheduled' | 'calendar' | 'engagement';
}

export function parseDrillDown(search: string | URLSearchParams): DrillDownState {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const out: DrillDownState = {};
  const strategy = params.get('strategy');
  const sprint = params.get('sprint');
  const topic = params.get('topic');
  const post = params.get('post');
  const subTab = params.get('cpTab') as DrillDownState['subTab'] | null;
  if (strategy) out.strategyId = strategy;
  if (sprint) out.sprintId = sprint;
  if (topic) out.topicId = topic;
  if (post) out.postId = post;
  if (subTab) out.subTab = subTab;
  return out;
}

export function buildDrillDownQuery(state: DrillDownState): string {
  const params = new URLSearchParams();
  applyDrillDownState(params, state);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function applyDrillDownState(params: URLSearchParams, state: DrillDownState): void {
  if (state.subTab) params.set('cpTab', state.subTab);
  else params.delete('cpTab');
  if (state.strategyId) params.set('strategy', state.strategyId);
  else params.delete('strategy');
  if (state.sprintId) params.set('sprint', state.sprintId);
  else params.delete('sprint');
  if (state.topicId) params.set('topic', state.topicId);
  else params.delete('topic');
  if (state.postId) params.set('post', state.postId);
  else params.delete('post');
}

/** Which level (1-3) the state represents — used to render the right component. */
export function currentLevel(state: DrillDownState): 0 | 1 | 2 | 3 {
  if (state.topicId) return 3;
  if (state.sprintId) return 2;
  if (state.strategyId) return 1;
  return 0;
}

/**
 * Build the path for the standalone Topic route
 * (`/project/:projectId/content-plan/topic/:topicId?tab=…`).
 *
 * Used by the "Open in tab" affordance on Backlog rows + Calendar topic
 * chips, and by `TopicDetailRoutePage` for navigation back-links. The
 * `tab` query param mirrors the in-dialog tab key on `TopicDetailPanel`.
 */
export function getTopicRoutePath(args: {
  projectId: string;
  topicId: string;
  tab?: 'overview' | 'research' | 'templates' | 'voice' | 'posts';
  authorId?: string | null;
}): string {
  const tab = args.tab ?? 'overview';
  const params = new URLSearchParams({ tab });
  if (args.authorId) params.set('author', args.authorId);
  return `/project/${args.projectId}/content-plan/topic/${args.topicId}?${params.toString()}`;
}

/** Breadcrumb labels derived from the state. Caller supplies resolver functions. */
export function breadcrumbLabels(
  state: DrillDownState,
  resolvers: {
    strategyName?: (id: string) => string;
    sprintName?: (id: string) => string;
    topicName?: (id: string) => string;
  },
): string[] {
  const crumbs: string[] = ['Content Plans'];
  if (state.strategyId) {
    crumbs.push(resolvers.strategyName?.(state.strategyId) ?? state.strategyId);
  }
  if (state.sprintId) {
    crumbs.push(resolvers.sprintName?.(state.sprintId) ?? state.sprintId);
  }
  if (state.topicId) {
    crumbs.push(resolvers.topicName?.(state.topicId) ?? state.topicId);
  }
  return crumbs;
}
